import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { switchMap, map, catchError, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { MwTransportSitesApiService } from 'app/services/transport-sites-api.service';
import * as transportSitesActions from '../actions/transport-sites.actions';
import * as notifyActions from 'app/store/general/actions/notify.actions';
import { Store } from '@ngrx/store';
import { getAddressesState } from '../reducers';
import * as addressesActions from '../actions/addresses.actions';
import { AddressFormatter } from 'app/components/map/map.models';

@Injectable()
export class TransportSitesEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<any>,
    private transportSitesApiService: MwTransportSitesApiService
  ) {}

  loadTransportSites$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(transportSitesActions.loadTransportSites),
      switchMap(({ loadOptions }) => {
        return this.transportSitesApiService.load(loadOptions).pipe(
          // Add formatted address
          map((response) => {
            response.results.map((item) => {
              item.formattedAddress = AddressFormatter.format(item);
              return item;
            });
            return response;
          }),
          map((response) => {
            return transportSitesActions.loadTransportSitesSuccess({
              response,
            });
          }),
          catchError((error) =>
            of(transportSitesActions.loadTransportSitesFailure({ error }))
          )
        );
      })
    );
  });

  createTransportSites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(transportSitesActions.createTransportSite),
      withLatestFrom(this.store$.select(getAddressesState)),
      switchMap(([{ transportSite }, state]) => {
        return this.transportSitesApiService
          .create({ ...transportSite, addressId: state.savedAddress.addressId })
          .pipe(
            switchMap((result) => [
              transportSitesActions.createTransportSiteSuccess({
                transportSite: result,
              }),
              notifyActions.notifySuccess({
                notification: {
                  phrase: 'OPERATION.add_transport_site_successful',
                },
              }),
              addressesActions.resetAddress(),
            ]),
            catchError((error) =>
              of(transportSitesActions.createTransportSiteFailure({ error }))
            )
          );
      })
    )
  );

  updateTransportSites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(transportSitesActions.updateTransportSite),
      switchMap(({ transportSite }) => {
        return this.transportSitesApiService.update(transportSite).pipe(
          switchMap((result) => [
            transportSitesActions.updateTransportSiteSuccess({
              transportSite: result,
            }),
            notifyActions.notifySuccess({
              notification: {
                phrase: 'OPERATION.update_transport_site_successful',
              },
            }),
          ]),
          catchError((error) =>
            of(transportSitesActions.updateTransportSiteFailure({ error }))
          )
        );
      })
    )
  );

  deleteTransportSites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(transportSitesActions.deleteTransportSite),
      switchMap(({ transportSite }) => {
        return this.transportSitesApiService.delete(transportSite).pipe(
          switchMap(() => [
            transportSitesActions.deleteTransportSiteSuccess(),
            notifyActions.notifySuccess({
              notification: {
                phrase: 'OPERATION.delete_transport_site_successful',
              },
            }),
          ]),
          catchError((error) =>
            of(transportSitesActions.deleteTransportSiteFailure({ error }))
          )
        );
      })
    )
  );
}
